/**
 * ZEN ERP — Form Editörü (Tam Yeniden Tasarım)
 * Yan yana: Sol = Ayarlar (Alan + Katalog), Sağ = Canlı Önizleme
 * Katalog yönetimi (kasa_rengi, motor, kumanda, mekanizma) entegre.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Eye, EyeOff, GripVertical, RotateCcw, Save, ChevronRight, ChevronDown,
  AlertCircle, Check, Settings2, Columns, Loader2, Info, Plus, Trash2,
  Tag, DollarSign, Search, X, Pencil, BookOpen, Layers, ArrowLeft,
  Package, ToggleLeft, ToggleRight, Star, Link2, Copy,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { ModuleIcon } from "../components/layout/Sidebar";
import {
  ProductForm, invalidateFormConfigCache,
  setDraftFormConfigOverride, clearDraftFormConfigOverride,
} from "../components/OrderForm";
import { computeLineTotal } from "../utils/quoteUtils";
import { setArtZipMatrices, setSkyZipMatrices } from "../artbeyPricing";

// ── Sabitler ──────────────────────────────────────────────────────────────────
const GROUP_LABELS = {
  stor: "Stor Perde", zebra: "Zebra Perde", dikey: "Dikey Perde",
  zip_art110_helio: "Zip Perde Art-110", skyzip_1400: "Sky Zip 1400",
  skyzip_1500: "Sky Zip 1500", plise14: "Plise 14mm (Cam Balkon)",
  plise25: "Plise 25mm",
  ahsap_jaluzi_25: "Ahşap Jaluzi 25mm", ahsap_jaluzi_50: "Ahşap Jaluzi 50mm",
  metal_jaluzi_16_25: "Metal Jaluzi", silhouette: "Silhouette",
  sineklik_dikey: "Dikey Sineklik", surme_sineklik: "Sürmeli Sineklik",
};

const FIELD_TYPES = {
  en: "number", boy: "number", adet: "number", birimFiyat: "number",
  fabric: "text", ralCode: "text", roomName: "text",
  controlSide: "select", control: "select", kasaRengi: "select",
  mekanizma: "select",
  zincir: "select", kasa: "select", kasaKumasKaplama: "select",
  altCita: "select", yanKanal: "select", storZigil: "select",
  etekModel: "select", reduktor: "select", yayliMekanizma: "select",
  cocukKilidi: "select", deluxElcek: "select", lazerEtek: "select",
  motor: "checkbox", kurdela: "checkbox", reduktorlu: "checkbox",
  egimli: "checkbox", geceGunduz: "checkbox", renkliKasa: "checkbox", cocukZigil: "checkbox",
  mounting: "button_group", system14: "button_group",
  cableDir: "button_grid",
};
const TYPE_LABELS = { number: "Sayı", text: "Metin", select: "Seçim", checkbox: "Onay", button_group: "Buton", button_grid: "Izgara" };
const TYPE_CLS = {
  number: "bg-orange-100 text-orange-700",
  text: "bg-sky-100 text-sky-700", select: "bg-violet-100 text-violet-700",
  checkbox: "bg-emerald-100 text-emerald-700", button_group: "bg-amber-100 text-amber-700",
  button_grid: "bg-rose-100 text-rose-700",
};
const CORE_FIELD_KEYS = new Set(["en", "boy", "adet", "birimFiyat"]);
const FIELDS_WITH_OPTIONS = new Set(["control", "controlSide", "mounting", "system14", "kasaRengi"]);
const CHECKBOX_PRICEABLE = new Set(["geceGunduz", "renkliKasa", "egimli", "reduktorlu", "kurdela"]);
const ROLLER_ACCESSORY_KEYS = ["zincir","kasa","kasaKumasKaplama","altCita","mekanizma","yanKanal","storZigil","etekModel","reduktor","yayliMekanizma","cocukKilidi","deluxElcek","lazerEtek"];
const FIELDS_NO_PRICING  = new Set(["control", "controlSide", "cableDir", "fabric", "ralCode", "roomName", "kasaRengi", ...ROLLER_ACCESSORY_KEYS]);

// Kayıtlı "row" değeri olmayan alanlara TEK SEFERLİK, geriye dönük uyumlu bir satır ataması yapar
// (En/Boy gibi yan yana alanlar aynı satırda kalır — mevcut görünüm asla değişmez).
// Bir kez atandıktan sonra "row" sabit veridir; genişlik değişikliği satırları KAYDIRMAZ.
const ROW_GROUPS = [
  { keys: ["en", "boy", "adet", "birimFiyat"], fallbackWidth: 50 },
  { keys: ROLLER_ACCESSORY_KEYS, fallbackWidth: 100 },
];
function assignDefaultRows(fields) {
  const next = fields.map(f => ({ ...f }));
  ROW_GROUPS.forEach(({ keys, fallbackWidth }) => {
    const subset = next.filter(f => keys.includes(f.key) && f.row == null).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    let rowIdx = 0, rowWidth = 0;
    subset.forEach(f => {
      const w = f.widthPct ?? (f.colSpan === 2 ? 100 : fallbackWidth);
      if (rowWidth > 0 && rowWidth + w > 100.5) { rowIdx += 1; rowWidth = 0; }
      f.row = rowIdx;
      rowWidth += w;
    });
  });
  return next;
}

// Form alanı → katalog kind eşlemesi (inline katalog için)
const FIELD_CATALOG_MAP = {
  motor:     "motor",
  control:   "control",
  kasaRengi: "kasa_rengi",
  mekanizma: "mekanizma",
};

// Katalog alanları (assets)
const CATALOG_KINDS = {
  kasa_rengi: { label: "Kasa Renkleri", icon: "🎨" },
  motor:      { label: "Motorlar",      icon: "⚙️" },
  control:    { label: "Kumandalar",    icon: "🎮" },
  mekanizma:  { label: "Mekanizmalar",  icon: "🔧" },
};
// Motor/Kumanda/Mekanizma için form bağlantısı seçenekleri
const GROUPS_FOR_ASSIGNMENT = [
  { slug: "stor",                 label: "Stor Perde" },
  { slug: "zebra",                label: "Zebra Perde" },
  { slug: "dikey",                label: "Dikey Perde" },
  { slug: "silhouette",           label: "Silhouette" },
  { slug: "plise14",              label: "PLİCELL 15mm" },
  { slug: "plise25",              label: "PLİCELL 20mm" },
  { slug: "zip_art110_helio",     label: "Dikey Zip Perde" },
  { slug: "skyzip_1400",          label: "Sky Zip" },
  { slug: "sineklik_dikey",       label: "Dikey/Yatay Plise Sineklik" },
  { slug: "ahsap_jaluzi_25",      label: "Ahşap Jaluzi 25mm" },
  { slug: "ahsap_jaluzi_50",      label: "Ahşap Jaluzi 50mm" },
  { slug: "metal_jaluzi_16_25",   label: "Metal Jaluzi 16-25mm" },
  { slug: "ciftli_sistem",        label: "Çiftli Sistem" },
  { slug: "dikey_ray",            label: "Dikey Perde Rayı" },
  { slug: "stor_sistem",          label: "Kumaşsız Stor Sistemi" },
  { slug: "sadece_kumas",         label: "Sadece Kumaş" },
  { slug: "sineklik_duble_yatay", label: "Duble Yatay Plise Sineklik" },
  { slug: "surme_sineklik",       label: "Sürme Sineklik" },
  { slug: "rustik",               label: "Rustik Perde" },
  { slug: "tul_fon_perde",        label: "Tül & Fon Perde" },
];
// Hangi gruplar hangi katalog alanlarını kullanır
const GROUP_CATALOGS = {
  stor: ["kasa_rengi", "motor", "control", "mekanizma"],
  zebra: ["kasa_rengi", "motor", "control", "mekanizma"],
  dikey: ["motor", "control"],
  zip_art110_helio: ["motor", "control"],
  skyzip_1400: ["motor", "control"],
  skyzip_1500: ["motor", "control"],
  plise14: ["mekanizma"],
  plise25: ["motor", "mekanizma"],
  silhouette: ["motor", "control", "kasa_rengi"],
  ahsap_jaluzi_25: ["kasa_rengi", "motor"],
  ahsap_jaluzi_50: ["kasa_rengi", "motor"],
};
// Motor asset group eşlemesi
const MOTOR_GROUP_MAP = {
  stor: "stor", zebra: "stor", silhouette: "stor",
  dikey: "dikey",
  zip_art110_helio: null, skyzip_1400: "tirajli_ray", skyzip_1500: "tirajli_ray",
  plise25: "jaluzi_plise", plise14: "jaluzi_plise",
  ahsap_jaluzi_25: "ahsap", ahsap_jaluzi_50: "ahsap",
};

// ── Drag & Drop yardımcı ──────────────────────────────────────────────────────
function useDragList(list, setList) {
  const dragIdx = useRef(null);
  const onDragStart = (i) => (e) => { dragIdx.current = i; e.dataTransfer.effectAllowed = "move"; };
  const onDragOver  = (i) => (e) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === i) return;
    const next = [...list];
    const [removed] = next.splice(dragIdx.current, 1);
    next.splice(i, 0, removed);
    dragIdx.current = i;
    setList(next.map((f, idx) => ({ ...f, order: idx })));
  };
  const onDrop = () => { dragIdx.current = null; };
  return { onDragStart, onDragOver, onDrop };
}

// ── Yardımcı: Fiyat Kural Metni ──────────────────────────────────────────────
function priceRuleText(pr) {
  if (!pr?.amount) return null;
  if (pr.type === "fixed") return `+${pr.amount} ${pr.currency || "TL"}/adet`;
  if (pr.type === "pct")   return `+${pr.amount}%`;
  if (pr.type === "m2")    return `+${pr.amount}/m²`;
  return null;
}

// ── Seçenek Satırı ────────────────────────────────────────────────────────────
function OptionRow({ opt, index, onChange, onDelete, showPricing, onDragStart, onDragOver, onDrop }) {
  const pr = opt.pricingRule;
  const [editing, setEditing] = useState(false);
  return (
    <div
      className="group flex items-center gap-1.5 py-1 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-[#131B2E] transition-colors"
      draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop}
      data-testid={`opt-row-${opt.value}`}
    >
      <GripVertical className="w-3 h-3 text-slate-300 shrink-0 cursor-grab" />
      {editing ? (
        <input autoFocus className="zen-input !py-0 !text-xs flex-1"
          value={opt.label}
          onChange={(e) => onChange({ ...opt, label: e.target.value, value: e.target.value })}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => e.key === "Enter" && setEditing(false)}
        />
      ) : (
        <span className="flex-1 text-xs text-slate-700 dark:text-slate-300 truncate">{opt.label}</span>
      )}
      {showPricing && pr?.amount && (
        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 shrink-0">{priceRuleText(pr)}</span>
      )}
      {showPricing && (
        <select className="text-[10px] border-0 bg-transparent text-slate-400 cursor-pointer shrink-0 focus:ring-0"
          value={pr?.type || ""}
          onChange={(e) => {
            if (!e.target.value) { onChange({ ...opt, pricingRule: null }); return; }
            onChange({ ...opt, pricingRule: { type: e.target.value, amount: pr?.amount || 0, currency: pr?.currency || "TL" } });
          }}
        >
          <option value="">Fiyat yok</option>
          <option value="fixed">+Sabit</option>
          <option value="pct">+%</option>
          <option value="m2">×m²</option>
        </select>
      )}
      {showPricing && pr?.type && (
        <input type="number" min="0"
          className="zen-input !py-0 !text-[10px] !w-14 text-right shrink-0"
          value={pr.amount || ""}
          onChange={(e) => onChange({ ...opt, pricingRule: { ...pr, amount: parseFloat(e.target.value) || 0 } })}
        />
      )}
      <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600 shrink-0">
        <Pencil className="w-3 h-3" />
      </button>
      <button onClick={onDelete} data-testid={`opt-delete-${opt.value}`}
        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0">
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

// ── Seçenek Yöneticisi ────────────────────────────────────────────────────────
function OptionsManager({ field, onChange }) {
  const [newLabel, setNewLabel] = useState("");
  const [optSearch, setOptSearch] = useState("");
  const opts = field.options || [];
  const showPricing = !FIELDS_NO_PRICING.has(field.key);
  const dragList = useDragList(opts, (newOpts) => onChange({ ...field, options: newOpts }));
  const filteredOpts = optSearch ? opts.filter(o => o.label.toLowerCase().includes(optSearch.toLowerCase())) : opts;

  const addOpt = () => {
    if (!newLabel.trim()) return;
    onChange({ ...field, options: [...opts, { value: newLabel.trim(), label: newLabel.trim(), pricingRule: null }] });
    setNewLabel("");
  };

  return (
    <div className="space-y-1.5" data-testid={`options-manager-${field.key}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1">
          <Tag className="w-3 h-3" /> Seçenekler ({opts.length})
          {field.key === "control" && <span className="text-amber-600 font-normal normal-case ml-1">— kumanda fiyatı ayrı kalem</span>}
        </div>
      </div>
      {opts.length > 4 && (
        <div className="relative mb-1">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
          <input className="zen-input !py-1 !text-xs !pl-7" placeholder="Seçenek ara..."
            value={optSearch} onChange={(e) => setOptSearch(e.target.value)} />
        </div>
      )}
      <div className="space-y-0.5 max-h-48 overflow-y-auto">
        {filteredOpts.map((opt, i) => (
          <OptionRow key={i} opt={opt} index={i} showPricing={showPricing}
            onChange={(updated) => onChange({ ...field, options: opts.map((o, idx) => idx === i ? updated : o) })}
            onDelete={() => onChange({ ...field, options: opts.filter((_, idx) => idx !== i) })}
            onDragStart={dragList.onDragStart(i)} onDragOver={dragList.onDragOver(i)} onDrop={dragList.onDrop}
          />
        ))}
        {filteredOpts.length === 0 && <div className="text-[10px] text-slate-400 italic py-1">Seçenek yok.</div>}
      </div>
      <div className="flex items-center gap-1 pt-1">
        <input className="zen-input !py-0.5 !text-xs flex-1" value={newLabel}
          data-testid={`new-option-input-${field.key}`}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addOpt()}
          placeholder="Yeni seçenek ekle..." />
        <button onClick={addOpt} disabled={!newLabel.trim()} data-testid={`new-option-add-${field.key}`}
          className="zen-btn !py-0.5 !px-2 !text-[10px] flex items-center gap-1 disabled:opacity-40">
          <Plus className="w-3 h-3" /> Ekle
        </button>
      </div>
    </div>
  );
}

// ── Checkbox Fiyat Editörü ────────────────────────────────────────────────────
function CheckboxPricingEditor({ field, onChange }) {
  const pr = field.pricingRule;
  return (
    <div data-testid={`checkbox-pricing-${field.key}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
        <DollarSign className="w-3 h-3" /> İşaretlenince eklenecek fiyat
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <select className="zen-input !py-0.5 !text-[10px] !w-28"
          data-testid={`checkbox-pricing-type-${field.key}`}
          value={pr?.type || ""}
          onChange={(e) => {
            if (!e.target.value) { onChange({ ...field, pricingRule: null }); return; }
            onChange({ ...field, pricingRule: { type: e.target.value, amount: pr?.amount || 0, currency: pr?.currency || "TL" } });
          }}>
          <option value="">Fiyatsız</option>
          <option value="fixed">+Sabit TL</option>
          <option value="pct">+%</option>
          <option value="m2">×m² ekle</option>
        </select>
        {pr?.type && (
          <>
            <input type="number" min="0" step="0.01"
              data-testid={`checkbox-pricing-amount-${field.key}`}
              className="zen-input !py-0.5 !text-xs !w-20 text-right"
              value={pr.amount || ""} placeholder="Miktar"
              onChange={(e) => onChange({ ...field, pricingRule: { ...pr, amount: parseFloat(e.target.value) || 0 } })} />
            {pr.type === "fixed" && (
              <select className="zen-input !py-0.5 !text-[10px] !w-14"
                value={pr.currency || "TL"}
                onChange={(e) => onChange({ ...field, pricingRule: { ...pr, currency: e.target.value } })}>
                <option>TL</option><option>EUR</option>
              </select>
            )}
            <span className="text-[10px] text-amber-600 font-medium">{priceRuleText(pr)}</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── Kumaş Hızlı Filtresi ─────────────────────────────────────────────────────
function FabricSubgroupFilter({ assets }) {
  const fabrics = useMemo(() => assets.filter(a => a.kind === "fabric"), [assets]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [search, setSearch] = useState("");
  const [subgroupAssignments, setSubgroupAssignments] = useState({});
  const [savingSlug, setSavingSlug] = useState(null);

  const groups = useMemo(() => {
    const g = {};
    fabrics.forEach(f => {
      const key = f.group || "(Diğer)";
      if (!g[key]) g[key] = [];
      g[key].push(f);
    });
    return g;
  }, [fabrics]);

  const sortedGroups = useMemo(
    () => Object.entries(groups).sort((a, b) => b[1].length - a[1].length),
    [groups]
  );
  const current = activeGroup || sortedGroups[0]?.[0];
  const items = groups[current] || [];
  const filtered = search
    ? items.filter(f => (f.label + f.code).toLowerCase().includes(search.toLowerCase()))
    : items;

  // Mevcut subgroup'un form atamalarını fabrics'den çek
  useEffect(() => {
    const init = {};
    sortedGroups.forEach(([g, gItems]) => {
      init[g] = gItems[0]?.groupSlugs || [];
    });
    setSubgroupAssignments(init);
  }, [groups]);

  const toggleProductGroup = async (fabricGroup, productSlug) => {
    const current_slugs = subgroupAssignments[fabricGroup] || [];
    const next = current_slugs.includes(productSlug)
      ? current_slugs.filter(s => s !== productSlug)
      : [...current_slugs, productSlug];
    setSubgroupAssignments(prev => ({ ...prev, [fabricGroup]: next }));
    setSavingSlug(fabricGroup);
    try {
      await api.post("/assets/bulk-group-slugs", { kind: "fabric", fabricGroup, groupSlugs: next });
      const msg = next.length === 0
        ? `"${fabricGroup}" hiçbir formda görünmeyecek`
        : `"${fabricGroup}" — ${next.length} form bağlandı`;
      toast.success(msg, { id: `fsg-${fabricGroup}` });
    } catch {
      setSubgroupAssignments(prev => ({ ...prev, [fabricGroup]: current_slugs }));
      toast.error("Kaydedilemedi");
    }
    finally { setSavingSlug(null); }
  };

  const clearSubgroupSlugs = async () => {
    setSubgroupAssignments(prev => ({ ...prev, [current]: [] }));
    setSavingSlug(current);
    try {
      await api.post("/assets/bulk-group-slugs", { kind: "fabric", fabricGroup: current, groupSlugs: [] });
      toast.success(`"${current}" hiçbir formda görünmeyecek`, { id: `fsg-${current}` });
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSavingSlug(null); }
  };

  if (fabrics.length === 0) return (
    <div className="text-[10px] text-slate-400 italic py-2">Kumaş kataloğu bulunamadı.</div>
  );

  const currentSlugs = subgroupAssignments[current] || [];

  return (
    <div data-testid="fabric-subgroup-filter">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
        <BookOpen className="w-3 h-3" /> Kumaş Kataloğu
        <span className="text-slate-400 font-normal normal-case tracking-normal">
          — {fabrics.length} kumaş · {sortedGroups.length} alt grup
        </span>
      </div>

      {/* Alt grup sekmeleri — yatay kaydır */}
      <div className="flex gap-1 overflow-x-auto scrollbar-none pb-1.5 mb-2">
        {sortedGroups.map(([g, gItems]) => (
          <button key={g} onClick={() => { setActiveGroup(g); setSearch(""); }}
            data-testid={`fabric-group-tab-${g}`}
            className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-bold whitespace-nowrap transition-colors border
              ${current === g
                ? "bg-[#E5322A] text-white border-[#E5322A]"
                : "bg-slate-50 dark:bg-slate-800/60 border-slate-200 dark:border-[#22304A] text-slate-500 hover:border-[#E5322A]/40 hover:text-[#E5322A]"}`}>
            {g} <span className="opacity-75">({gItems.length})</span>
            {(subgroupAssignments[g] || []).length > 0 && (
              <span className="ml-1 text-[8px] opacity-80">🔗</span>
            )}
          </button>
        ))}
      </div>

      {/* Grup içi arama */}
      <div className="relative mb-1.5">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
        <input className="zen-input !py-1 !text-xs !pl-7"
          placeholder={`${current} grubunda ara...`}
          value={search} onChange={(e) => setSearch(e.target.value)} />
        {search && <button onClick={() => setSearch("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
          <X className="w-3 h-3" />
        </button>}
      </div>

      {/* Kumaş listesi */}
      <div className="max-h-40 overflow-y-auto space-y-0.5 rounded-lg border border-slate-100 dark:border-[#1A2235] p-1">
        {filtered.map(f => (
          <div key={f.id} className="flex items-center gap-1.5 py-1 px-1.5 rounded hover:bg-slate-50 dark:hover:bg-[#0D1220] transition-colors">
            <span className="flex-1 text-[11px] text-slate-700 dark:text-slate-300 truncate">{f.label}</span>
            {f.code && (
              <span className="text-[9px] font-mono text-slate-400 shrink-0 bg-slate-100 dark:bg-slate-800 px-1 rounded">{f.code}</span>
            )}
            {f.price > 0 && (
              <span className="text-[9px] text-amber-600 shrink-0 font-semibold">{f.price}</span>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-[10px] text-slate-400 py-2 text-center italic">Sonuç bulunamadı.</div>
        )}
      </div>
      <div className="text-[9px] text-slate-400 mt-1 mb-2">
        {filtered.length} / {items.length} kumaş · toplam {fabrics.length}
      </div>

      {/* ── Form Ataması ── */}
      <div className="border-t border-slate-100 dark:border-[#22304A] pt-2 mt-1" data-testid="fabric-assignment-panel">
        <div className="text-[9px] font-bold uppercase text-slate-400 mb-1.5 flex items-center gap-1">
          <Link2 className="w-3 h-3" />
          "{current}" hangi formlarda çıksın?
          {savingSlug === current && <span className="font-normal normal-case text-slate-400 ml-1">kaydediliyor…</span>}
        </div>
        {currentSlugs.length === 0 && (
          <div className="text-[9px] text-rose-500 dark:text-rose-400 italic mb-1.5">
            Hiçbir form seçilmedi — bu kumaş grubu HİÇBİR sipariş formunda görünmeyecek
          </div>
        )}
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
          {GROUPS_FOR_ASSIGNMENT.map(g => {
            const checked = currentSlugs.includes(g.slug);
            return (
              <label key={g.slug} className="flex items-center gap-1.5 cursor-pointer group/chk py-0.5"
                data-testid={`fabric-assign-check-${current}-${g.slug}`}>
                <div onClick={() => toggleProductGroup(current, g.slug)}
                  className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 transition-colors cursor-pointer
                    ${checked ? "bg-[#E5322A] border-[#E5322A]" : "border-slate-300 dark:border-[#31405D] group-hover/chk:border-[#E5322A]/60"}`}>
                  {checked && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <span onClick={() => toggleProductGroup(current, g.slug)}
                  className={`text-[10px] cursor-pointer transition-colors ${checked ? "text-slate-700 dark:text-slate-200 font-semibold" : "text-slate-500 group-hover/chk:text-slate-700"}`}>
                  {g.label}
                </span>
              </label>
            );
          })}
        </div>
        {currentSlugs.length > 0 && (
          <button onClick={clearSubgroupSlugs} className="mt-1.5 text-[9px] text-rose-500 hover:underline" data-testid="fabric-assign-clear">
            Tüm formlardan kaldır (hiçbir formda görünmez)
          </button>
        )}
      </div>
    </div>
  );
}

// ── Alan Detay Paneli (sol panelin içi) ───────────────────────────────────────
function FieldDetailPanel({ field, allGroups, allConfigs, onBack, onChange, onGroupVisibilityChange,
  assets, groupSlug, onAssetAdd, onAssetUpdate, onAssetDelete }) {
  const type = FIELD_TYPES[field.key] || "text";
  const hasOptions = FIELDS_WITH_OPTIONS.has(field.key);
  const hasPricing = CHECKBOX_PRICEABLE.has(field.key);
  const catalogKind = FIELD_CATALOG_MAP[field.key] || null;
  const catalogMeta = catalogKind ? CATALOG_KINDS[catalogKind] : null;

  // Grup filtresi: her grupta bu alan görünür mü?
  const groupVisibility = allGroups.reduce((acc, g) => {
    const cfg = allConfigs[g.slug];
    if (!cfg) { acc[g.slug] = false; return acc; }
    const f = cfg.fields?.find(x => x.key === field.key);
    acc[g.slug] = f?.visible || false;
    return acc;
  }, {});

  const hintFor = (key) => {
    if (key === "required") return "Bu alan seçilmeden sipariş kaydedilemez";
    if (key === "hidden") return "Bu alan sipariş formunda görünmez";
    if (key === "colSpan2") return "Bu alan 2 kolon genişliğinde görünür";
    return null;
  };

  return (
    <div className="flex flex-col h-full" data-testid={`field-detail-${field.key}`}>
      {/* Başlık */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-[#22304A] shrink-0">
        <button onClick={onBack} className="w-7 h-7 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-600">
          <ArrowLeft className="w-3.5 h-3.5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-slate-800 dark:text-white">{field.label}</div>
          <div className="flex items-center gap-1.5 mt-0.5">
            <span className="font-mono text-[10px] text-slate-400">{field.key}</span>
            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${TYPE_CLS[type] || TYPE_CLS.text}`}>
              {TYPE_LABELS[type] || type}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Çekirdek alan notu */}
        {CORE_FIELD_KEYS.has(field.key) && (
          <div className="px-3 py-2.5 rounded-xl bg-red-50 dark:bg-red-900/15 border border-red-200 dark:border-red-800/40 flex items-start gap-2">
            <span className="text-[#E5322A] text-sm font-black mt-0.5">★</span>
            <div>
              <div className="text-[10px] font-bold text-red-700 dark:text-red-300">Çekirdek Alan</div>
              <div className="text-[9px] text-red-600/70 dark:text-red-400/70">Bu alan sipariş hesaplamalarında kullanılır. Önizlemede sıralama/boyutlandırma tam çalışır.</div>
            </div>
          </div>
        )}
        {/* Temel Ayarlar */}
        <div className="space-y-2">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Temel Ayarlar</div>
          {/* Etiket */}
          <div className="py-2 px-3 rounded-xl bg-slate-50 dark:bg-[#0D1220] space-y-1.5">
            <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Etiket</div>
            <input
              type="text"
              data-testid={`field-label-input-${field.key}`}
              value={field.label}
              onChange={(e) => onChange({ ...field, label: e.target.value })}
              className="w-full text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-[#22304A] bg-white dark:bg-[#0B0F1A] text-slate-700 dark:text-slate-200 outline-none focus:border-[#E5322A]"
            />
          </div>
          {/* Görünürlük */}
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50 dark:bg-[#0D1220]">
            <div>
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Bu Grupta Görünür</div>
              <div className="text-[10px] text-slate-400">{field.visible ? "Sipariş formunda görünür" : "Sipariş formunda gizli"}</div>
            </div>
            <button data-testid={`toggle-visible-${field.key}`}
              onClick={() => onChange({ ...field, visible: !field.visible })}
              className={`shrink-0 transition-colors ${field.visible ? "text-emerald-500" : "text-slate-300"}`}>
              {field.visible ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
            </button>
          </div>
          {/* Zorunlu */}
          <div className="flex items-center justify-between py-2 px-3 rounded-xl bg-slate-50 dark:bg-[#0D1220]">
            <div>
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Zorunlu Alan</div>
              <div className="text-[10px] text-slate-400">{field.required ? "⚠ Seçilmeden sipariş kaydedilemez" : "İsteğe bağlı"}</div>
            </div>
            <button data-testid={`toggle-required-${field.key}`}
              onClick={() => onChange({ ...field, required: !field.required })}
              className={`shrink-0 transition-colors ${field.required ? "text-red-500" : "text-slate-300"}`}>
              {field.required ? <ToggleRight className="w-8 h-8" /> : <ToggleLeft className="w-8 h-8" />}
            </button>
          </div>
          {/* Alan Genişliği — % slider */}
          <div className="py-2 px-3 rounded-xl bg-slate-50 dark:bg-[#0D1220] space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-300">Alan Genişliği</div>
              <span className="text-[11px] font-mono font-bold text-[#E5322A]">
                {field.widthPct ?? (field.colSpan === 2 ? 100 : (ROLLER_ACCESSORY_KEYS.includes(field.key) ? 100 : 50))}%
              </span>
            </div>
            <input type="range" min={20} max={100} step={5}
              data-testid={`field-width-slider-${field.key}`}
              value={field.widthPct ?? (field.colSpan === 2 ? 100 : (ROLLER_ACCESSORY_KEYS.includes(field.key) ? 100 : 50))}
              onChange={(e) => { const v = Number(e.target.value); onChange({ ...field, widthPct: v, colSpan: v >= 90 ? 2 : 1 }); }}
              className="w-full accent-[#E5322A] h-1.5 rounded-full cursor-pointer"
            />
            <div className="flex gap-1 flex-wrap">
              {[25, 33, 50, 67, 75, 100].map(p => (
                <button key={p} type="button"
                  data-testid={`field-width-preset-${field.key}-${p}`}
                  onClick={() => onChange({ ...field, widthPct: p, colSpan: p >= 90 ? 2 : 1 })}
                  className={`text-[9px] font-bold px-1.5 py-0.5 rounded border transition-colors ${(field.widthPct ?? (field.colSpan === 2 ? 100 : (ROLLER_ACCESSORY_KEYS.includes(field.key) ? 100 : 50))) === p ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-200 text-slate-500 hover:border-[#E5322A] hover:text-[#E5322A]"}`}>
                  {p}%
                </button>
              ))}
            </div>
            <div className="text-[9px] text-slate-400">Önizlemede kenardan sürükleyerek de ayarlayabilirsiniz</div>
          </div>
        </div>

        {/* Grup Filtresi */}
        {allGroups.length > 0 && (
          <div className="space-y-2">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1">
              <Layers className="w-3 h-3" /> Hangi Gruplarda Görünür
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {allGroups.map(g => (
                <label key={g.slug}
                  data-testid={`group-filter-${field.key}-${g.slug}`}
                  className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg cursor-pointer border transition-colors text-xs
                    ${groupVisibility[g.slug]
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800"
                      : "bg-slate-50 border-slate-200 text-slate-500 dark:bg-[#0D1220] dark:border-[#1A2235]"}`}>
                  <input type="checkbox" className="sr-only"
                    checked={!!groupVisibility[g.slug]}
                    onChange={(e) => onGroupVisibilityChange(field.key, g.slug, e.target.checked)}
                  />
                  {groupVisibility[g.slug]
                    ? <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                    : <div className="w-3 h-3 rounded border border-slate-300 shrink-0" />}
                  <span className="truncate text-[10px] font-medium">{g.name || GROUP_LABELS[g.slug] || g.slug}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Seçenek Yönetimi */}
        {hasOptions && (
          <div className="border-t border-slate-200 dark:border-[#22304A] pt-4">
            <OptionsManager field={field} onChange={onChange} />
          </div>
        )}

        {/* Checkbox Fiyat */}
        {hasPricing && (
          <div className="border-t border-slate-200 dark:border-[#22304A] pt-4">
            <CheckboxPricingEditor field={field} onChange={onChange} />
          </div>
        )}

        {/* Inline Katalog Yönetimi (motor, kumanda, kasa rengi, mekanizma için) */}
        {catalogKind && catalogMeta && (
          <div className="border-t border-slate-200 dark:border-[#22304A] pt-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
              <span className="text-base leading-none">{catalogMeta.icon}</span>
              {catalogMeta.label} Yönetimi
              <span className="text-slate-400 font-normal normal-case tracking-normal ml-1">
                — Katalog bölümüyle tam senkron
              </span>
            </div>
            <CatalogKindPanel
              kind={catalogKind}
              kindMeta={catalogMeta}
              assets={assets.filter(a => {
                if (a.kind !== catalogKind) return false;
                if (catalogKind === "motor") {
                  const mg = MOTOR_GROUP_MAP[groupSlug];
                  return !mg || !a.group || a.group === mg || a.group === "";
                }
                return true;
              })}
              groupSlug={groupSlug}
              onAdd={onAssetAdd}
              onUpdate={onAssetUpdate}
              onDelete={onAssetDelete}
            />
          </div>
        )}

        {/* Kumaş Hızlı Filtresi (fabric alanı için) */}
        {field.key === "fabric" && (
          <div className="border-t border-slate-200 dark:border-[#22304A] pt-4">
            <FabricSubgroupFilter assets={assets} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Katalog Asset Satırı ──────────────────────────────────────────────────────
function AssetRow({ asset, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [localLabel, setLocalLabel] = useState(asset.label);
  const [localPrice, setLocalPrice] = useState(asset.price || "");
  const [showGroups, setShowGroups] = useState(false);
  const [groupSlugs, setGroupSlugs] = useState(asset.groupSlugs || []);
  const [savingGroups, setSavingGroups] = useState(false);

  const save = async () => {
    try {
      await api.put(`/assets/${asset.id}`, { label: localLabel, price: parseFloat(localPrice) || 0 });
      onUpdate({ ...asset, label: localLabel, price: parseFloat(localPrice) || 0 });
      setEditing(false);
      toast.success("Güncellendi", { id: `ast-${asset.id}` });
    } catch { toast.error("Güncellenemedi"); }
  };

  const toggleGroup = async (slug) => {
    const next = groupSlugs.includes(slug)
      ? groupSlugs.filter(s => s !== slug)
      : [...groupSlugs, slug];
    setGroupSlugs(next);
    setSavingGroups(true);
    try {
      await api.put(`/assets/${asset.id}`, { groupSlugs: next });
      onUpdate({ ...asset, groupSlugs: next });
    } catch { toast.error("Kaydedilemedi"); setGroupSlugs(asset.groupSlugs || []); }
    finally { setSavingGroups(false); }
  };

  const clearAll = async () => {
    setGroupSlugs([]);
    setSavingGroups(true);
    try {
      await api.put(`/assets/${asset.id}`, { groupSlugs: [] });
      onUpdate({ ...asset, groupSlugs: [] });
    } catch { toast.error("Kaydedilemedi"); setGroupSlugs(asset.groupSlugs || []); }
    finally { setSavingGroups(false); }
  };

  return (
    <div data-testid={`asset-row-${asset.id}`}>
      <div className="group flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 dark:hover:bg-[#0D1220] transition-colors">
        {editing ? (
          <>
            <input autoFocus className="zen-input !py-0 !text-xs flex-1"
              value={localLabel} onChange={(e) => setLocalLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()} />
            <input type="number" className="zen-input !py-0 !text-xs !w-16 text-right"
              value={localPrice} onChange={(e) => setLocalPrice(e.target.value)}
              placeholder="0" onKeyDown={(e) => e.key === "Enter" && save()} />
            <span className="text-[10px] text-slate-400">{asset.currency || "TL"}</span>
            <button onClick={save} className="text-emerald-500 hover:text-emerald-700"><Check className="w-3.5 h-3.5" /></button>
            <button onClick={() => setEditing(false)} className="text-slate-400 hover:text-slate-600"><X className="w-3.5 h-3.5" /></button>
          </>
        ) : (
          <>
            <span className="flex-1 text-xs text-slate-700 dark:text-slate-300 truncate">{asset.label}</span>
            {asset.price > 0 && (
              <span className="text-[10px] shrink-0 text-amber-600">{asset.price} {asset.currency || "TL"}</span>
            )}
            {/* Bağlı form sayısı */}
            {groupSlugs.length > 0 ? (
              <span className="text-[9px] shrink-0 px-1 py-0.5 rounded bg-[#E5322A]/10 text-[#E5322A] font-bold border border-[#E5322A]/20"
                title="Bu asset sadece seçili formlarda görünür">
                {groupSlugs.length} form
              </span>
            ) : (
              <span className="text-[9px] shrink-0 text-slate-300 dark:text-slate-600 italic">tüm</span>
            )}
            {/* Form bağlantısı butonu */}
            <button onClick={() => setShowGroups(g => !g)} data-testid={`asset-link-${asset.id}`}
              className={`opacity-0 group-hover:opacity-100 shrink-0 transition-opacity ${showGroups ? "text-[#E5322A] opacity-100" : "text-slate-400 hover:text-[#E5322A]"}`}
              title="Hangi formlarda görünsün?">
              <Link2 className="w-3 h-3" />
            </button>
            <button onClick={() => setEditing(true)}
              className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-slate-600 shrink-0 transition-opacity">
              <Pencil className="w-3 h-3" />
            </button>
            <button onClick={() => onDelete(asset.id)} data-testid={`asset-delete-${asset.id}`}
              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 shrink-0 transition-opacity">
              <Trash2 className="w-3 h-3" />
            </button>
          </>
        )}
      </div>

      {/* Form bağlantısı paneli */}
      {showGroups && (
        <div className="mx-2 mb-2 px-3 py-2 rounded-lg bg-slate-50 dark:bg-[#0D1220] border border-slate-200 dark:border-[#22304A] space-y-2"
          data-testid={`asset-group-panel-${asset.id}`}>
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
              <Link2 className="w-3 h-3" />
              Hangi formlarda görünsün?
              {savingGroups && <span className="text-[9px] text-slate-400 font-normal normal-case ml-1">kaydediliyor…</span>}
            </div>
            {groupSlugs.length > 0 && (
              <button onClick={clearAll} className="text-[9px] text-slate-400 hover:text-rose-500 transition-colors">
                Tümünü kaldır
              </button>
            )}
          </div>
          {groupSlugs.length === 0 && (
            <div className="text-[10px] text-rose-500 dark:text-rose-400 italic">
              Hiçbir form seçilmedi — bu kumaş HİÇBİR sipariş formunda görünmeyecek
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1">
            {GROUPS_FOR_ASSIGNMENT.map(g => (
              <label key={g.slug} className="flex items-center gap-1.5 cursor-pointer group/chk"
                data-testid={`asset-group-check-${asset.id}-${g.slug}`}>
                <div onClick={() => toggleGroup(g.slug)}
                  className={`w-3.5 h-3.5 rounded border-2 flex items-center justify-center shrink-0 transition-colors cursor-pointer
                    ${groupSlugs.includes(g.slug)
                      ? "bg-[#E5322A] border-[#E5322A]"
                      : "border-slate-300 dark:border-[#31405D] group-hover/chk:border-[#E5322A]/60"}`}>
                  {groupSlugs.includes(g.slug) && <Check className="w-2.5 h-2.5 text-white" />}
                </div>
                <span onClick={() => toggleGroup(g.slug)}
                  className={`text-[10px] cursor-pointer transition-colors ${groupSlugs.includes(g.slug) ? "text-slate-700 dark:text-slate-200 font-semibold" : "text-slate-500 group-hover/chk:text-slate-700"}`}>
                  {g.label}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Katalog Bölümü (kasa_rengi, motor, kumanda vb.) ───────────────────────────
function CatalogKindPanel({ kind, kindMeta, assets, groupSlug, onAdd, onUpdate, onDelete }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [addMode, setAddMode] = useState(false);
  const [adding, setAdding] = useState(false);

  const filtered = search
    ? assets.filter(a => a.label.toLowerCase().includes(search.toLowerCase()))
    : assets;

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setAdding(true);
    try {
      const { data } = await api.post("/assets", {
        kind,
        label: newLabel.trim(),
        price: parseFloat(newPrice) || 0,
        currency: "TL",
        group: kind === "motor" ? (MOTOR_GROUP_MAP[groupSlug] || "") : "",
        code: "",
      });
      onAdd(data);
      setNewLabel(""); setNewPrice(""); setAddMode(false);
      toast.success(`${newLabel} eklendi`);
    } catch { toast.error("Eklenemedi"); }
    finally { setAdding(false); }
  };

  return (
    <div className="border border-slate-200 dark:border-[#22304A] rounded-xl overflow-hidden"
      data-testid={`catalog-panel-${kind}`}>
      <button
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-[#0D1220] transition-colors"
        onClick={() => setOpen(o => !o)}
        data-testid={`catalog-toggle-${kind}`}>
        <div className="flex items-center gap-2">
          <span className="text-base leading-none">{kindMeta.icon}</span>
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{kindMeta.label}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500">{assets.length}</span>
        </div>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 dark:border-[#1A2235] px-3 pb-3 pt-2 space-y-2">
          {assets.length > 5 && (
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
              <input className="zen-input !py-1 !text-xs !pl-7" placeholder="Ara..."
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          )}
          <div className="max-h-52 overflow-y-auto space-y-0.5">
            {filtered.map(a => (
              <AssetRow key={a.id} asset={a} onUpdate={onUpdate} onDelete={onDelete} />
            ))}
            {filtered.length === 0 && <div className="text-[10px] text-slate-400 italic py-1">Kayıt yok.</div>}
          </div>
          {addMode ? (
            <div className="flex items-center gap-1.5 pt-1">
              <input autoFocus className="zen-input !py-0.5 !text-xs flex-1"
                value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                placeholder="Yeni kayıt adı..." />
              <input type="number" className="zen-input !py-0.5 !text-xs !w-16 text-right"
                value={newPrice} onChange={(e) => setNewPrice(e.target.value)}
                placeholder="Fiyat" onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
              <button onClick={handleAdd} disabled={adding || !newLabel.trim()}
                data-testid={`catalog-add-${kind}`}
                className="zen-btn !py-0.5 !px-2 !text-[10px] flex items-center gap-1 disabled:opacity-40">
                {adding ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
              </button>
              <button onClick={() => setAddMode(false)} className="text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button onClick={() => setAddMode(true)}
              data-testid={`catalog-new-${kind}`}
              className="w-full text-left flex items-center gap-1.5 text-[10px] text-[#E5322A] hover:text-[#c52620] font-semibold py-1 px-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
              <Plus className="w-3 h-3" /> Yeni {kindMeta.label.slice(0, -1)} Ekle
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ── Ana Sayfa ─────────────────────────────────────────────────────────────────
export default function FormEditorPage() {
  const [groups, setGroups]               = useState([]);
  const [selectedSlug, setSelectedSlug]   = useState("stor");
  const [fields, setFields]               = useState([]);
  const [allConfigs, setAllConfigs]       = useState({});
  const [assets, setAssets]               = useState([]);
  const [loading, setLoading]             = useState(false);
  const [saving, setSaving]               = useState(false);
  const [dirty, setDirty]                 = useState(false);
  const [selectedFieldKey, setSelField]   = useState(null);
  const [dragging, setDragging]           = useState(null);
  const [globalSearch, setGlobalSearch]   = useState("");

  // Form klonlama
  const [cloneModal, setCloneModal]       = useState(false);
  const [cloneName, setCloneName]         = useState("");
  const [cloneSlug, setCloneSlug]         = useState("");
  const [cloning, setCloning]             = useState(false);

  // Canlı Önizleme — GERÇEK sipariş formu (ProductForm) örnek veriyle
  const [previewItem, setPreviewItem]     = useState(null);
  const [matrices, setMatrices]           = useState([]);
  const [rates, setRates]                 = useState({ eurToTl: 38, usdToTl: 34 });
  const [formFabricGroupsMap, setFormFabricGroupsMap] = useState(null);

  // Tüm assetleri yükle
  const loadAssets = useCallback(async () => {
    try {
      const { data } = await api.get("/assets");
      setAssets(data);
    } catch {}
  }, []);

  // Fiyatlandırma için matris + kur bilgisi (gerçek formun ihtiyaç duyduğu veriler)
  useEffect(() => {
    api.get("/matrices").then(({ data }) => {
      setMatrices(data || []);
      setArtZipMatrices(data || []);
      setSkyZipMatrices(data || []);
    }).catch(() => {});
    api.get("/rates").then(({ data }) => setRates(data)).catch(() => {});
    api.get("/settings?category=form_config").then(({ data }) => {
      const entry = (data || []).find(d => d.key === "form_fabric_groups");
      if (entry) setFormFabricGroupsMap(entry.value);
    }).catch(() => {});
  }, []);

  // Tüm grup configlerini yükle
  const loadAllConfigs = useCallback(async () => {
    try {
      const { data } = await api.get("/form-configs-all");
      setAllConfigs(data || {});
    } catch {}
  }, []);

  // Seçili grubun config'ini yükle
  const loadConfig = useCallback(async (slug) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/form-configs/${slug}`);
      setFields(assignDefaultRows(data.fields || []));
      setDirty(false);
    } catch { toast.error("Konfigürasyon yüklenemedi"); }
    finally { setLoading(false); }
  }, []);

  // Ürün gruplarını yükle
  useEffect(() => {
    api.get("/product-groups").then(({ data }) => {
      setGroups(data.filter(g => g.active !== false));
    }).catch(() => setGroups([]));
    loadAllConfigs();
    loadAssets();
  }, [loadAllConfigs, loadAssets]);

  // Form klonlama handler
  const handleClone = async () => {
    if (!cloneName.trim() || !cloneSlug.trim()) { toast.error("İsim ve slug gerekli"); return; }
    setCloning(true);
    try {
      const origGroup = groups.find(g => g.slug === selectedSlug);
      if (!origGroup) throw new Error("Orijinal form bulunamadı");
      // 1) product_groups klonu
      await api.post(`/product-groups/${origGroup.id}/clone`, { slug: cloneSlug.trim(), name: cloneName.trim() });
      // 2) form konfigürasyonu klonu
      await api.post(`/form-configs/${selectedSlug}/clone`, { newSlug: cloneSlug.trim() });
      toast.success(`"${cloneName}" formu oluşturuldu — Ürün Ekle menüsünde görünecek`);
      setCloneModal(false); setCloneName(""); setCloneSlug("");
      // Grupları yenile
      const { data } = await api.get("/product-groups");
      setGroups(data.filter(g => g.active !== false));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Klonlama başarısız");
    } finally { setCloning(false); }
  };

  useEffect(() => {
    if (selectedSlug) { loadConfig(selectedSlug); setSelField(null); }
  }, [selectedSlug, loadConfig]);

  // Seçili grubun gerçek ürün grubu kaydı (yoksa geçici bir kayıt üret)
  const currentGroup = useMemo(() => {
    return groups.find(g => g.slug === selectedSlug)
      || { slug: selectedSlug, name: GROUP_LABELS[selectedSlug] || selectedSlug, currency: "TL", defaultKdv: 20, basePricePerM2: 0, minM2: 0, minWidthCm: 0, maxWidthCm: 0, minHeightCm: 0, maxHeightCm: 0, roundingStepCm: 10 };
  }, [groups, selectedSlug]);

  // Gerçek sipariş formunun (ProductForm) beklediği örnek "item" — Sipariş ekranındaki başlangıç değerleriyle birebir
  const buildPreviewItem = useCallback((grp) => {
    const isDikeyGrp = grp.slug === "dikey";
    return {
      groupSlug: grp.slug, product: grp.name, currency: grp.currency || "TL",
      unitPrice: grp.basePricePerM2 || 0, unit: "m²",
      kdvRate: grp.defaultKdv ?? 20,
      qty: 1, widthCm: 0, heightCm: 0,
      kasaOlcusu: grp.slug === "surme_sineklik" ? "40x40" : "",
      fabric: "", fabricCode: "", fabricPrice: 0, fabricCurrency: "", ralCode: "", cableDir: "", mounting: "", midFoot: false,
      pliseExtras: [], extrasLabel: "", renkSayisi: 2,
      motor: false, motorLabel: isDikeyGrp ? "Dikey Motor Standart" : "", motorPrice: isDikeyGrp ? 200 : 0, motorCurrency: isDikeyGrp ? "EUR" : (grp.currency || "TL"),
      control: "", controlLabel: "", controlPrice: 0,
      controlSide: "",
      kurdela: false, reduktorlu: false, egimli: false,
      system14: "", geceGunduz: false, renkliKasa: false, roomName: "", note: "",
      profilRengi: "", boya: "", memfez: "",
      zipCesidi: "", sistemTuru: "", balKodu: "", balDurumu: "", montajliKiris: "", logoBaski: "", fabricBrand: "", motorChosen: false,
      dolu: "", kasaRengi: "", kasaKumandaKaplama: "", ciftCamArasi: "",
      urun: "", reduktor: "", zigilSistem: "", dikeyTipi: "", rayTipi: "", slatGenisligi: "", kasaTipi: "", zincirTipi: "",
      slatSize: "", slatVariantKey: "", slatColor: "", slatColorCode: "",
      slaytTipi: isDikeyGrp ? "8.9" : "",
      slatLeftCount: 0, slatLeftFabric: "",
      slatMiddleCount: 0, slatMiddleFabric: "",
      slatRightCount: 0, slatRightFabric: "",
      lineTotal: 0,
    };
  }, []);

  // Grup değişince önizleme kalemini sıfırla
  useEffect(() => {
    setPreviewItem(buildPreviewItem(currentGroup));
  }, [selectedSlug, currentGroup, buildPreviewItem]);

  // Henüz kaydedilmemiş alan değişikliklerini gerçek forma anlık yansıt
  useEffect(() => {
    if (!selectedSlug) return;
    setDraftFormConfigOverride(selectedSlug, { groupSlug: selectedSlug, fields });
    return () => clearDraftFormConfigOverride(selectedSlug);
  }, [selectedSlug, fields]);

  // Alan meta verisi değiştikçe önizlemeyi yeniden kur (etiket/sıra/görünürlük/genişlik)
  const previewConfigKey = useMemo(
    () => fields.map(f => `${f.key}:${f.label}:${f.visible}:${f.required}:${f.order}:${f.colSpan}:${f.widthPct}:${f.row}`).join("|"),
    [fields]
  );

  // Alan değiştiğinde (tek alan için)
  const handleFieldChange = (updatedField) => {
    setFields(prev => prev.map(f => f.key === updatedField.key ? updatedField : f));
    setDirty(true);
  };

  // Canlı önizlemede sürükle/boyutlandır ile tetiklenen kısmi (patch) güncelleme
  const handleFieldMetaChange = useCallback((key, patch) => {
    setFields(prev => prev.map(f => f.key === key ? { ...f, ...patch } : f));
    setDirty(true);
  }, []);

  // Drag-drop sıralama (sol panel)
  const { onDragStart, onDragOver, onDrop } = useDragList(fields, (newFields) => {
    setFields(newFields); setDirty(true);
  });

  // Grup görünürlük değiştir (cross-group)
  const handleGroupVisibilityChange = async (fieldKey, targetSlug, visible) => {
    // Hedef grubun config'ini al ya da yeni oluştur
    let targetCfg = allConfigs[targetSlug];
    if (!targetCfg) {
      try {
        const { data } = await api.get(`/form-configs/${targetSlug}`);
        targetCfg = data;
      } catch { return; }
    }
    const updatedFields = (targetCfg.fields || []).map(f =>
      f.key === fieldKey ? { ...f, visible } : f
    );
    // Eğer field yoksa ekle
    if (!updatedFields.find(f => f.key === fieldKey)) {
      updatedFields.push({ key: fieldKey, label: fieldKey, visible, required: false, order: 99, colSpan: 1 });
    }
    const updated = { ...targetCfg, fields: updatedFields };
    try {
      await api.put(`/form-configs/${targetSlug}`, updated);
      setAllConfigs(prev => ({ ...prev, [targetSlug]: updated }));
      invalidateFormConfigCache(targetSlug);
      // Eğer hedef == seçili grup, lokali güncelle
      if (targetSlug === selectedSlug) {
        setFields(updatedFields);
        setDirty(false);
      }
      toast.success(`${GROUP_LABELS[targetSlug] || targetSlug}: ${visible ? "Görünür" : "Gizli"}`, { id: `gviz-${fieldKey}-${targetSlug}` });
    } catch { toast.error("Grup ayarı kaydedilemedi"); }
  };

  // Kaydet
  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/form-configs/${selectedSlug}`, {
        groupSlug: selectedSlug,
        fields: fields.map((f, i) => ({ ...f, order: i })),
      });
      invalidateFormConfigCache(selectedSlug);
      setAllConfigs(prev => ({ ...prev, [selectedSlug]: { groupSlug: selectedSlug, fields } }));
      setDirty(false);
      toast.success("Konfigürasyon kaydedildi", { id: "form-cfg-save" });
    } catch { toast.error("Kaydetme başarısız"); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm("Bu grubun ayarlarını fabrika varsayılanlarına döndürmek istiyor musunuz?")) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/form-configs/${selectedSlug}/reset`);
      invalidateFormConfigCache(selectedSlug);
      setFields(data.fields || []);
      setDirty(false);
      toast.success("Varsayılana döndürüldü");
    } catch { toast.error("Sıfırlama başarısız"); }
    finally { setSaving(false); }
  };

  // Assets bu gruba göre filtrele
  const getKindAssets = (kind) => {
    const motorGroup = MOTOR_GROUP_MAP[selectedSlug];
    if (kind === "motor" && motorGroup) {
      return assets.filter(a => a.kind === kind && (!a.group || a.group === motorGroup || a.group === ""));
    }
    return assets.filter(a => a.kind === kind);
  };

  const catalogKinds = GROUP_CATALOGS[selectedSlug] || [];

  const selectedField = selectedFieldKey ? fields.find(f => f.key === selectedFieldKey) : null;
  const currentGroupName = groups.find(g => g.slug === selectedSlug)?.name || GROUP_LABELS[selectedSlug] || selectedSlug;
  const visibleCount = fields.filter(f => f.visible).length;

  // Arama filtresi
  const filteredFields = globalSearch
    ? fields.filter(f => f.label.toLowerCase().includes(globalSearch.toLowerCase()) || f.key.toLowerCase().includes(globalSearch.toLowerCase()))
    : fields;
  const visibleSorted = filteredFields.filter(f => f.visible);
  const hiddenSorted  = filteredFields.filter(f => !f.visible);

  return (
    <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 155px)' }} data-testid="form-editor-page">
      {/* ─── SOL PANEL ─────────────────────────────────────────── */}
      <div className="w-[58%] flex flex-col min-h-0 border-r border-slate-200 dark:border-[#22304A]">

        {/* Grup Seçici */}
        <div className="shrink-0 border-b border-slate-200 dark:border-[#22304A]">
          <div className="flex overflow-x-auto scrollbar-none px-3 pt-2 gap-1 pb-0">
            {groups.map(g => (
              <button key={g.slug} data-testid={`group-tab-${g.slug}`}
                onClick={() => setSelectedSlug(g.slug)}
                className={`shrink-0 px-3 py-1.5 rounded-t-lg text-[10px] font-semibold transition-colors whitespace-nowrap
                  ${selectedSlug === g.slug
                    ? "bg-white dark:bg-[#0C1220] border-t border-l border-r border-slate-200 dark:border-[#22304A] text-[#E5322A]"
                    : "text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-[#131B2E]"}`}>
                {g.name || GROUP_LABELS[g.slug] || g.slug}
              </button>
            ))}
            {/* Kopyala (Klonla) Butonu */}
            <button onClick={() => { setCloneName(""); setCloneSlug(""); setCloneModal(true); }} title="Bu formu kopyala"
              data-testid="form-clone-btn"
              className="shrink-0 px-3 py-1.5 rounded-t-lg text-[10px] font-semibold transition-colors whitespace-nowrap text-slate-400 hover:text-[#E5322A] hover:bg-slate-100 dark:hover:bg-[#131B2E] flex items-center gap-1">
              <Copy className="w-3 h-3" /> Kopyala
            </button>
          </div>
        </div>

        {/* Klonlama Modal */}
        {cloneModal && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40" onClick={() => setCloneModal(false)}>
            <div className="bg-white dark:bg-[#0C1220] rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 space-y-4" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-800 dark:text-white text-sm">Form Kopyala</h3>
                  <p className="text-xs text-slate-500 mt-0.5">"{currentGroupName}" formunu şu anda kopyalıyorsunuz</p>
                </div>
                <button onClick={() => setCloneModal(false)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 block mb-1">Yeni Form Adı</label>
                  <input className="zen-input w-full" placeholder="PLİCELL 14mm Özel" value={cloneName}
                    onChange={(e) => { setCloneName(e.target.value); if (!cloneSlug) setCloneSlug(e.target.value.toLowerCase().replace(/\s+/g,"_").replace(/[^a-z0-9_]/g,"").slice(0,32)); }}
                    data-testid="clone-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-300 block mb-1">Slug (teknik ad)</label>
                  <input className="zen-input w-full font-mono" placeholder="plise14_ozel" value={cloneSlug}
                    onChange={(e) => setCloneSlug(e.target.value.replace(/\s+/g,"_").replace(/[^a-z0-9_]/g,""))}
                    data-testid="clone-slug-input" />
                  <p className="text-[10px] text-slate-400 mt-1">Küçük harf, alt çizgi, rakam — benzersiz olmalı</p>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setCloneModal(false)} className="zen-btn-ghost flex-1 !text-xs">İptal</button>
                <button onClick={handleClone} disabled={cloning || !cloneName.trim() || !cloneSlug.trim()}
                  className="zen-btn flex-1 !text-xs flex items-center justify-center gap-1.5" data-testid="clone-confirm-btn">
                  {cloning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
                  Kopyala ve Oluştur
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Arama + Başlık + Eylemler */}
        <div className="shrink-0 px-4 py-3 border-b border-slate-200 dark:border-[#22304A] flex items-center gap-3">
          {selectedField ? (
            <div className="flex-1 text-sm font-bold text-slate-700 dark:text-white truncate">
              {currentGroupName} — {selectedField.label}
            </div>
          ) : (
            <>
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input className="zen-input !py-1.5 !text-xs !pl-8 w-full"
                  placeholder="Alan ara (kumaş, motor, montaj...)"
                  value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} />
                {globalSearch && <button onClick={() => setGlobalSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>}
              </div>
              <button onClick={handleReset} disabled={saving} title="Varsayılana döndür"
                className="zen-btn-ghost !p-1.5 text-slate-500 hover:text-slate-700">
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleSave} disabled={!dirty || saving} data-testid="btn-save-config"
                className={`zen-btn !py-1.5 !px-3 !text-xs flex items-center gap-1.5 transition-opacity ${!dirty ? "opacity-40 cursor-not-allowed" : ""}`}>
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Kaydet{dirty ? " *" : ""}
              </button>
            </>
          )}
        </div>

        {/* İçerik */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : selectedField ? (
            /* ── Alan Detay Paneli ── */
            <FieldDetailPanel
              field={selectedField}
              allGroups={groups}
              allConfigs={allConfigs}
              onBack={() => setSelField(null)}
              onChange={(updated) => { handleFieldChange(updated); }}
              onGroupVisibilityChange={handleGroupVisibilityChange}
              assets={assets}
              groupSlug={selectedSlug}
              onAssetAdd={(newAsset) => setAssets(prev => [...prev, newAsset])}
              onAssetUpdate={(updated) => setAssets(prev => prev.map(a => a.id === updated.id ? updated : a))}
              onAssetDelete={async (id) => {
                if (!window.confirm("Bu katalog öğesi silinsin mi?")) return;
                try {
                  await api.delete(`/assets/${id}`);
                  setAssets(prev => prev.filter(a => a.id !== id));
                  toast.success("Silindi");
                } catch { toast.error("Silinemedi"); }
              }}
            />
          ) : (
            /* ── Alan + Katalog Listesi ── */
            <div className="p-4 space-y-5">
              {/* Alan Ayarları bölümü */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
                    <Settings2 className="w-3 h-3" /> Alan Ayarları
                  </div>
                  <span className="text-[10px] text-slate-400">({visibleCount} görünür)</span>
                </div>
                {/* Görünür Alanlar */}
                {visibleSorted.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {visibleSorted.map((f, i) => {
                      const globalIdx = fields.findIndex(x => x.key === f.key);
                      return (
                        <div key={f.key} data-testid={`field-card-${f.key}`}
                          draggable
                          onDragStart={(e) => { setDragging(f.key); onDragStart(globalIdx)(e); }}
                          onDragOver={(e) => { onDragOver(globalIdx)(e); }}
                          onDrop={(e) => { setDragging(null); onDrop(e); }}
                          className={`flex items-center gap-2 px-3 py-2 rounded-xl border transition-all cursor-pointer group
                            bg-white dark:bg-[#131B2E] border-slate-200 dark:border-[#22304A]
                            hover:border-[#E5322A]/40 hover:shadow-sm
                            ${dragging === f.key ? "opacity-50 ring-2 ring-[#E5322A]" : ""}`}
                          onClick={() => setSelField(f.key)}>
                          <GripVertical className="w-3 h-3 text-slate-300 shrink-0 cursor-grab" onClick={(e) => e.stopPropagation()} />
                          <button
                            data-testid={`toggle-visible-${f.key}`}
                            onClick={(e) => { e.stopPropagation(); handleFieldChange({ ...f, visible: false }); }}
                            className="w-6 h-6 rounded-lg flex items-center justify-center bg-emerald-100 text-emerald-600 hover:bg-emerald-200 shrink-0">
                            <Eye className="w-3 h-3" />
                          </button>
                          <span className="flex-1 text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{f.label}</span>
                          {CORE_FIELD_KEYS.has(f.key) && (
                            <span className="shrink-0 text-[8px] font-black text-[#E5322A] opacity-60 leading-none" title="Çekirdek alan">★</span>
                          )}
                          <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold hidden sm:block ${TYPE_CLS[FIELD_TYPES[f.key]] || TYPE_CLS.text}`}>
                            {TYPE_LABELS[FIELD_TYPES[f.key]] || "?"}
                          </span>
                          {f.required && <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />}
                          {(f.options?.some(o => o.pricingRule?.amount) || f.pricingRule?.amount) && (
                            <Star className="w-3 h-3 text-amber-400 shrink-0" />
                          )}
                          <ChevronRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 shrink-0" />
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Gizli Alanlar */}
                {hiddenSorted.length > 0 && (
                  <div>
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold mb-1 mt-3 flex items-center gap-1">
                      <EyeOff className="w-2.5 h-2.5" /> Gizli ({hiddenSorted.length})
                    </div>
                    <div className="space-y-1">
                      {hiddenSorted.map((f) => (
                        <div key={f.key} data-testid={`field-card-${f.key}`}
                          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200/60 dark:border-[#1A2235] opacity-60 cursor-pointer group hover:opacity-100 hover:border-[#E5322A]/40 transition-all"
                          onClick={() => setSelField(f.key)}>
                          <button
                            data-testid={`toggle-visible-${f.key}`}
                            onClick={(e) => { e.stopPropagation(); handleFieldChange({ ...f, visible: true }); }}
                            className="w-6 h-6 rounded-lg flex items-center justify-center bg-slate-100 text-slate-400 hover:bg-emerald-100 hover:text-emerald-600 shrink-0">
                            <EyeOff className="w-3 h-3" />
                          </button>
                          <span className="flex-1 text-xs text-slate-500 truncate">{f.label}</span>
                          <ChevronRight className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100 shrink-0" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {filteredFields.length === 0 && (
                  <div className="text-center text-slate-400 text-xs py-6">"{globalSearch}" için sonuç bulunamadı.</div>
                )}
              </div>

              {/* Katalog Yönetimi bölümü */}
              {catalogKinds.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
                    <BookOpen className="w-3 h-3" /> Katalog Yönetimi
                    <span className="text-slate-400 font-normal normal-case tracking-normal">— bu grup için seçenekler</span>
                  </div>
                  <div className="space-y-2">
                    {catalogKinds.map(kind => {
                      const meta = CATALOG_KINDS[kind];
                      if (!meta) return null;
                      const kindAssets = getKindAssets(kind);
                      return (
                        <CatalogKindPanel key={kind} kind={kind} kindMeta={meta}
                          assets={kindAssets} groupSlug={selectedSlug}
                          onAdd={(newAsset) => setAssets(prev => [...prev, newAsset])}
                          onUpdate={(updated) => setAssets(prev => prev.map(a => a.id === updated.id ? updated : a))}
                          onDelete={async (id) => {
                            if (!window.confirm("Bu katalog öğesi silinsin mi?")) return;
                            try {
                              await api.delete(`/assets/${id}`);
                              setAssets(prev => prev.filter(a => a.id !== id));
                              toast.success("Silindi");
                            } catch { toast.error("Silinemedi"); }
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─── SAĞ PANEL: Canlı Önizleme — GERÇEK sipariş formu, tek kod bloğu ── */}
      <div className="flex-1 min-w-0 h-full flex flex-col zen-hc-surface border-l border-slate-200 dark:border-[#22304A]" data-testid="form-editor-live-preview">
        <div className="px-4 py-3 border-b border-slate-200/70 dark:border-white/10 shrink-0">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
            <Eye className="w-3 h-3" /> Canlı Önizleme
            <span className="text-[9px] text-slate-400 dark:text-slate-500 font-normal normal-case tracking-normal ml-1">— gerçek sipariş formu, örnek veriyle</span>
          </div>
          <div className="text-sm font-bold text-slate-800 dark:text-white mt-0.5 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-[#E5322A] inline-block"></span>
            {currentGroupName} Formu
          </div>
        </div>
        <div className="flex-1 overflow-y-auto zen-quote-builder">
          {previewItem ? (
            <ProductForm
              key={`${selectedSlug}|${previewConfigKey}`}
              group={currentGroup}
              item={previewItem}
              setItem={setPreviewItem}
              assets={assets}
              productGroups={groups}
              onSwitchGroup={(g) => setSelectedSlug(g.slug)}
              editingItemIndex={null}
              onCancel={() => {}}
              onAdd={() => toast.info("Bu bir önizlemedir — gerçek sipariş 'Sipariş Oluştur' ekranından yapılır.", { id: "form-editor-preview-add" })}
              computeLineTotal={(g, w, h, q, e) => computeLineTotal(g, w, h, q, e, matrices, rates)}
              targetCur={currentGroup.currency || "TL"}
              rates={rates}
              matrices={matrices}
              hideTopBack
              editorMode
              editorSelectedKey={selectedFieldKey}
              onFieldSelect={setSelField}
              onFieldMetaChange={handleFieldMetaChange}
              formFabricGroupsMap={formFabricGroupsMap}
            />
          ) : (
            <div className="p-8 text-center text-xs text-slate-400">Yükleniyor…</div>
          )}
        </div>
      </div>
    </div>
  );
}
